<template>
  <div class="area-select-box">
    <el-select
      :model-value="values[0]"
      :placeholder="provPlaceholder"
      clearable
      filterable
      autocomplete="nony"
      @change="onProvChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v) in provinces"
        :key="v.xzqhdm"
        :value="v.xzqhdm"
        :label="v.name"
      />
    </el-select>
    <el-select
      :model-value="values[1]"
      :disabled="isNull(values[0])"
      class="asb-city"
      :placeholder="cityPlaceholder"
      clearable
      filterable
      autocomplete="nony"
      @change="onCityChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v) in cities"
        :key="v.xzqhdm"
        :value="v.xzqhdm"
        :label="v.name"
      />
    </el-select>
    <el-select
      v-if="enableTown"
      class="asb-town"
      :style="`visibility: ${noTown ? 'hidden' : 'visible'}`"
      :model-value="values[2]"
      :disabled="isNull(values[1]) || noTown"
      clearable
      :placeholder="townPlaceholder"
      @change="onTownChange"
    >
      <el-option
        v-for="(v) in towns"
        :key="v.xzqhdm"
        :value="v.xzqhdm"
        :label="v.name"
      />
    </el-select>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { isNull } from '@core/tools';
import { getSystemCitys } from '../../api/system/base/citys';
import { ElMessage } from 'element-plus';

defineOptions({ name: 'CurrentAreaSelect' });

export interface IBaseAddr {
  name: string;
  xzqhdm: string;
  children?: IBaseAddr[];
}

const emit = defineEmits<{
  'key-enter': [];
  change: [value: string[], withoutTown?: boolean];
  'names-change': [value: string[]];
}>();

const props = defineProps({
  initialCodes: {
    type: Array as PropType<string[]>,
    default() {
      return [];
    },
  },
  enableTown: {
    type: Boolean,
    default: true,
  },
  enableEnterKey: {
    type: Boolean,
    default: false,
  },
  provPlaceholder: {
    type: String,
    default: '请选择省',
  },
  cityPlaceholder: {
    type: String,
    default: '请选择市',
  },
  townPlaceholder: {
    type: String,
    default: '请选择区',
  },
});

const values = ref<string[]>([]);
const names = ref<string[]>([]);

const provinces = ref([] as IBaseAddr[]);
const cities = ref([] as IBaseAddr[]);
const towns = ref([] as IBaseAddr[]);

const noTown = ref(false);

function onProvChange(provCode: string) {
  values.value[0] = provCode;
  values.value[1] = undefined;
  values.value[2] = undefined;
  cities.value = provinces.value.find((v) => v.xzqhdm === provCode)?.children || [];

  names.value = [provinces.value.find((v) => v.xzqhdm === provCode)?.name || '', '', ''];

  emit('change', [values.value[0], undefined, undefined]);
  emit('names-change', names.value);
}

function onCityChange(cityCode: string) {
  values.value[1] = cityCode;
  values.value[2] = undefined;
  towns.value = cities.value.find((v) => v.xzqhdm === cityCode)?.children || [];

  noTown.value = towns.value?.length === 0;

  names.value[1] = cities.value.find((v) => v.xzqhdm === cityCode)?.name || '';
  names.value[2] = towns.value?.find((v) => v.xzqhdm === cityCode)?.name || '';

  emit('change', [values.value[0], values.value[1], undefined], noTown.value);
  emit('names-change', names.value);
}

function onTownChange(townCode: string) {
  values.value[2] = townCode;
  names.value[2] = towns.value?.find((v) => v.xzqhdm === townCode)?.name || '';

  emit('change', values.value, false);
  emit('names-change', names.value);
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  emit('key-enter');
}

async function initAreaSelect() {
  try {

    const data = await getSystemCitys();

    // @ts-ignore
    provinces.value = data as IBaseAddr[];

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function initData() {
  const codes = props.initialCodes;

  values.value = codes;
  noTown.value = codes.length === 2 || codes[1] && codes[2] && codes[1] === codes[2];

  if (!provinces.value.length) {
    await initAreaSelect();
  }

  if (codes.length > 0) {
    if (codes[0] && codes[1]) {
      cities.value = provinces.value.find((v) => v.xzqhdm === codes[0])?.children || [];
    }

    if (codes[0] && codes[1] && codes[2]) {
      towns.value = cities.value.find((v) => v.xzqhdm === codes[1])?.children || [];
    }

    values.value = codes.map((v) => isNull(v) ? undefined : v);

    if (codes[0]) names.value[0] = provinces.value.find((v) => v.xzqhdm === codes[0])?.name || '';
    if (codes[1]) names.value[1] = cities.value.find((v) => v.xzqhdm === codes[1])?.name || '';
    if (codes[2]) names.value[2] = towns.value?.find((v) => v.xzqhdm === codes[2])?.name || '';

    emit('names-change', names.value);
  }
}

watch(
  () => props.initialCodes,
  () => {
    initData();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.area-select-box {
  display: flex;
  width: 100%;

  .el-select {
    flex: 1 1;
  }
}

:deep(.asb-city),
:deep(.asb-town) {
  margin-left: 9px;
}
</style>
